import { useState } from "react";
import { useNavigate } from "react-router";
import { ANREDEN, ANSPRACHFORMEN } from "@dj/domain/gemeinsam";
import { Knopf } from "../design/Kachel";
import { Auswahl, Textfeld } from "../design/Formular";
import { Abweisung, Meldung, Status } from "../design/Hinweis";
import { Eingang } from "../design/Ansicht";
import { zugang } from "../plattform/api";
import { geraetebezeichnungVorschlag, passkeyUnterstuetzt } from "../plattform/geraet";
import { usePasskeyzeremonie } from "./passkey";
import { OHNE_NETZ, passkeymeldung, REGISTRIERUNG_OHNE_PASSKEY as OHNE_PASSKEY } from "./zugangsmeldungen";

/** Feste Wortlaute (oberflaeche.md §9). */
const KONTAKT_PFLICHT = "Für die Registrierung eines Reviers ist ein bestätigter Wiederherstellungskontakt Pflicht.";

/**
 * Registrierung (oberflaeche.md §7, identitaeten.md §4): Angaben → „Senden“ (Code anfordern) → „Einmalcode“ →
 * „Bestätigen“ (Code und Passkey erstellen). Die Angaben prüft der Dienst; die App prüft nur, ob das Gerät Passkeys kann.
 */
export function Registrierung() {
  const navigate = useNavigate();
  const zeremonie = usePasskeyzeremonie("registrierung");
  const [schritt, setSchritt] = useState<"angaben" | "code">("angaben");
  const [reviername, setReviername] = useState("");
  const [name, setName] = useState("");
  const [anrede, setAnrede] = useState<string | null>(null);
  const [kontakt, setKontakt] = useState("");
  const [ansprachform, setAnsprachform] = useState<string | null>(null);
  const [geraet, setGeraet] = useState(geraetebezeichnungVorschlag);
  const [code, setCode] = useState("");
  const [vorgang, setVorgang] = useState<string | null>(null);
  const [grund, setGrund] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function senden() {
    setGrund(null);
    setMeldung(null);
    if (!passkeyUnterstuetzt()) {
      setMeldung(OHNE_PASSKEY);
      return;
    }
    setLaeuft(true);
    const angaben: Record<string, unknown> = {};
    if (reviername) angaben["Reviername"] = reviername;
    if (name) angaben["Name"] = name;
    if (anrede) angaben["Anrede"] = anrede;
    if (kontakt) angaben["Wiederherstellungskontakt"] = kontakt;
    if (ansprachform) angaben["Ansprachform"] = ansprachform;
    const a = await zugang("registrierung/beginnen", angaben);
    setLaeuft(false);
    if (a.netzfehler) {
      setMeldung(OHNE_NETZ);
      return;
    }
    if (!a.ok) {
      setGrund(a.grund ?? "unzulaessige-angaben");
      return;
    }
    setVorgang(String(a.felder["Registrierungsvorgang"]));
    setSchritt("code");
  }

  /** Startet die Zeremonie im Handler des Fingertipps (Passkeyzeremonie); der Code gilt mit den Optionen als verbraucht. */
  function bestaetigen() {
    setGrund(null);
    setMeldung(null);
    setLaeuft(true);
    void zeremonie.ausfuehren({ zweck: "registrierung", Registrierungsvorgang: vorgang, Einmalcode: code, "Gerätebezeichnung": geraet }).then((r) => {
      setLaeuft(false);
      if (r.fehler === "abgelehnt") setGrund(r.antwort.grund ?? "unzulaessige-angaben");
      else if (r.fehler) {
        setMeldung(passkeymeldung("registrierung", r.fehler));
        // Der Code ist mit den Optionen verbraucht; nach Ablauf der Zeremonie fordert „Senden“ einen neuen an.
        if (r.fehler === "zeitueberschreitung") setSchritt("angaben");
      }
      else navigate(r.antwort.kennungen["Revier"] ? `/reviere/${r.antwort.kennungen["Revier"]}` : "/", { replace: true });
    });
  }

  return (
    <Eingang name="Registrierung" titel="Registrierung" unter="Revier registrieren" schritt={schritt} unten={schritt === "angaben" ? (
      <Knopf symbol="senden" art="planung" onPress={senden} isDisabled={laeuft}>Senden</Knopf>
    ) : (
      <div className="knopfreihe">
        <Knopf symbol="passkey" art="planung" onPress={bestaetigen} isDisabled={laeuft}>Bestätigen</Knopf>
        <Knopf art="planung-zweit" onPress={() => setMeldung(KONTAKT_PFLICHT)}>Später</Knopf>
      </div>
    )}>
      {schritt === "angaben" ? (
        <form className="formular text-bereich" onSubmit={(e) => { e.preventDefault(); void senden(); }}>
          <Textfeld label="Reviername" wert={reviername} onChange={setReviername} autoComplete="organization" />
          <Textfeld label="Name" wert={name} onChange={setName} autoComplete="name" />
          <Auswahl label="Anrede" optionen={ANREDEN} wert={anrede} onChange={setAnrede} />
          <Textfeld label="Wiederherstellungskontakt" wert={kontakt} onChange={setKontakt} hilfe="Telefonnummer oder E-Mail-Adresse" autoComplete="tel" />
          <Auswahl label="Ansprachform" optionen={ANSPRACHFORMEN} wert={ansprachform} onChange={setAnsprachform} />
          <Textfeld label="Gerätebezeichnung" wert={geraet} onChange={setGeraet} />
          <Abweisung grund={grund} />
          {meldung ? <Meldung>{meldung}</Meldung> : null}
        </form>
      ) : (
        <form className="formular text-bereich" onSubmit={(e) => { e.preventDefault(); bestaetigen(); }}>
          <Status symbol="nachricht">Code an {kontakt} gesendet</Status>
          <Textfeld label="Einmalcode" wert={code} onChange={setCode} eingabemodus="numeric" autoComplete="one-time-code" mono />
          <Textfeld label="Gerätebezeichnung" wert={geraet} onChange={setGeraet} />
          <Abweisung grund={grund} />
          {meldung ? <Meldung>{meldung}</Meldung> : null}
        </form>
      )}
    </Eingang>
  );
}
